import React, { useState, useMemo, useCallback } from 'react';

const PrintNREMTCustomModuleBuilder = ({ isOpen, onClose, onSave, existingData }) => {
  const [activeTab, setActiveTab] = useState('predefined'); // 'predefined' or 'custom'
  const [editingCell, setEditingCell] = useState(null); // For cell popup editor
  const [customModule, setCustomModule] = useState({
    moduleName: '',
    section: '',
    gridRows: 3,
    gridColumns: 2,
    totalPoints: 5,
    gridCells: [] // Initialize empty, will be populated based on rows/columns
  });

  // Load existing data when component opens
  React.useEffect(() => {
    if (isOpen && existingData) {
      setCustomModule({
        moduleName: existingData.moduleName || '',
        section: existingData.section || '',
        gridRows: existingData.gridRows || 3,
        gridColumns: existingData.gridColumns || 2,
        totalPoints: existingData.totalPoints || 5,
        gridCells: existingData.gridCells || []
      });
      setActiveTab('custom'); // Switch to custom tab when editing
    } else if (isOpen && !existingData) {
      // Reset for new module
      setCustomModule({
        moduleName: '',
        section: '',
        gridRows: 3,
        gridColumns: 2,
        totalPoints: 5,
        gridCells: []
      });
      setActiveTab('predefined');
    }
  }, [isOpen, existingData]);

  // Predefined NREMT modules based on your actual forms
  const predefinedModules = useMemo(() => [
    {
      id: 'vital-signs',
      moduleName: 'Vital Signs Assessment',
      section: 'Patient Assessment',
      gridRows: 3,
      gridColumns: 3,
      totalPoints: 9,
      gridCells: [
        { id: 0, type: 'text', content: 'BP' },
        { id: 1, type: 'blank', content: '' },
        { id: 2, type: 'text', content: 'Pulse' },
        { id: 3, type: 'blank', content: '' },
        { id: 4, type: 'text', content: 'Resp' },
        { id: 5, type: 'blank', content: '' },
        { id: 6, type: 'text', content: 'O2 Sat' },
        { id: 7, type: 'blank', content: '' },
        { id: 8, type: 'checkbox', content: '' }
      ],
      tasks: [
        { text: 'Obtains baseline blood pressure', critical: false },
        { text: 'Obtains pulse rate and quality', critical: false },
        { text: 'Obtains respiratory rate and quality', critical: false },
        { text: 'Assesses lung sounds', critical: false },
        { text: 'Obtains oxygen saturation', critical: false },
        { text: 'Assesses pupils', critical: false },
        { text: 'Checks blood glucose level', critical: false },
        { text: 'Assesses JVD (jugular vein distention)', critical: false },
        { text: 'Assesses peripheral edema', critical: false }
      ]
    },
    {
      id: 'scene-safety',
      moduleName: 'Scene Safety & BSI',
      section: 'Scene Assessment',
      gridRows: 2,
      gridColumns: 2,
      totalPoints: 4,
      gridCells: [
        { id: 0, types: ['text', 'checkbox'], content: 'BSI' },
        { id: 1, types: ['text', 'checkbox'], content: 'Scene Safety' },
        { id: 2, types: ['text', 'checkbox'], content: 'Mechanism/Nature' },
        { id: 3, types: ['text', 'checkbox'], content: 'Patient Count' }
      ],
      tasks: [
        { text: 'Takes/verbalizes BSI precautions', critical: true },
        { text: 'Determines scene safety', critical: true },
        { text: 'Determines mechanism/nature of illness', critical: false },
        { text: 'Determines number of patients', critical: false }
      ]
    },
    {
      id: 'airway-management',
      moduleName: 'Airway Management',
      section: 'Airway & Breathing',
      gridRows: 2,
      gridColumns: 2,
      totalPoints: 4,
      gridCells: [
        { id: 0, types: ['text', 'checkbox'], content: 'Open Airway' },
        { id: 1, types: ['text', 'checkbox'], content: 'Suction' },
        { id: 2, types: ['text', 'checkbox'], content: 'Insert Adjunct' },
        { id: 3, types: ['text', 'checkbox'], content: 'Ventilation' }
      ],
      tasks: [
        { text: 'Opens airway manually', critical: true },
        { text: 'Elevates tongue, inserts simple adjunct', critical: false },
        { text: 'Suctions airway if needed', critical: false },
        { text: 'Ventilates patient appropriately', critical: true }
      ]
    },
    {
      id: 'opqrst-history',
      moduleName: 'OPQRST History',
      section: 'History Taking',
      gridRows: 2,
      gridColumns: 3,
      totalPoints: 6,
      gridCells: [
        { id: 0, type: 'text', content: 'O - Onset' },
        { id: 1, type: 'text', content: 'P - Provocation' },
        { id: 2, type: 'text', content: 'Q - Quality' },
        { id: 3, type: 'text', content: 'R - Radiation' },
        { id: 4, type: 'text', content: 'S - Severity' },
        { id: 5, type: 'text', content: 'T - Time' }
      ],
      tasks: [
        { text: 'O - Onset of symptoms', critical: false },
        { text: 'P - Provocation/Palliation', critical: false },
        { text: 'Q - Quality of pain/symptoms', critical: false },
        { text: 'R - Radiation of pain', critical: false },
        { text: 'S - Severity (1-10 scale)', critical: false },
        { text: 'T - Time/Timeline', critical: false }
      ]
    },
    {
      id: 'sample-history',
      moduleName: 'SAMPLE History',
      section: 'History Taking',
      gridRows: 2,
      gridColumns: 3,
      totalPoints: 6,
      gridCells: [
        { id: 0, type: 'text', content: 'S - Signs' },
        { id: 1, type: 'text', content: 'A - Allergies' },
        { id: 2, type: 'text', content: 'M - Medications' },
        { id: 3, type: 'text', content: 'P - Past Hx' },
        { id: 4, type: 'text', content: 'L - Last Intake' },
        { id: 5, type: 'text', content: 'E - Events' }
      ],
      tasks: [
        { text: 'S - Signs and symptoms', critical: false },
        { text: 'A - Allergies', critical: false },
        { text: 'M - Medications', critical: false },
        { text: 'P - Past medical history', critical: false },
        { text: 'L - Last oral intake', critical: false },
        { text: 'E - Events leading to injury/illness', critical: false }
      ]
    },
    {
      id: 'secondary-assessment',
      moduleName: 'Secondary Assessment',
      section: 'Physical Exam',
      gridRows: 4,
      gridColumns: 2,
      totalPoints: 8,
      gridCells: [
        { id: 0, type: 'text', content: 'Head' },
        { id: 1, type: 'blank', content: '' },
        { id: 2, type: 'text', content: 'Neck' },
        { id: 3, type: 'blank', content: '' },
        { id: 4, type: 'text', content: 'Chest' },
        { id: 5, type: 'blank', content: '' },
        { id: 6, type: 'text', content: 'Abdomen' },
        { id: 7, type: 'blank', content: '' }
      ],
      tasks: [
        { text: 'Inspects and palpates head', critical: false },
        { text: 'Inspects and palpates neck', critical: false },
        { text: 'Inspects and palpates chest', critical: false },
        { text: 'Inspects and palpates abdomen', critical: false },
        { text: 'Inspects and palpates pelvis', critical: false },
        { text: 'Inspects and palpates extremities', critical: false },
        { text: 'Assesses posterior body', critical: false },
        { text: 'Manages secondary injuries', critical: false }
      ]
    }
  ], []);

  // Initialize grid cells when rows/columns change
  React.useEffect(() => {
    const totalCells = customModule.gridRows * customModule.gridColumns;
    
    setCustomModule(prev => {
      const currentCellCount = prev.gridCells.length;
      
      // Only update if the number of cells needed has changed
      if (totalCells !== currentCellCount) {
        const newGridCells = Array.from({ length: totalCells }, (_, index) => ({
          id: index,
          types: ['blank'], // Support for multiple types
          content: ''
        }));
        
        // Preserve existing cell data if available and convert old format
        const preservedCells = newGridCells.map((cell, index) => {
          const existingCell = prev.gridCells[index];
          if (existingCell) {
            // Convert old single type format to new multi-type format
            if (existingCell.type && !existingCell.types) {
              return { 
                ...cell, 
                ...existingCell, 
                types: [existingCell.type],
                type: undefined // Remove old property
              };
            }
            return { ...cell, ...existingCell };
          }
          return cell;
        });
        
        return {
          ...prev,
          gridCells: preservedCells
        };
      }
      
      return prev;
    });
  }, [customModule.gridRows, customModule.gridColumns]);

  const handlePredefinedModuleSelect = useCallback((module) => {
    onSave(module);
    onClose();
  }, [onSave, onClose]);

  const handleGridCellChange = useCallback((cellIndex, field, value) => {
    setCustomModule(prev => {
      const newGridCells = [...prev.gridCells];
      newGridCells[cellIndex] = { ...newGridCells[cellIndex], [field]: value };
      return { ...prev, gridCells: newGridCells };
    });
  }, []);

  const handleCellTypeToggle = useCallback((type) => {
    if (editingCell) {
      const currentTypes = editingCell.cell.types || [editingCell.cell.type || 'blank'];
      let newTypes;
      
      if (currentTypes.includes(type)) {
        // Remove the type if it's already selected
        newTypes = currentTypes.filter(t => t !== type);
        if (newTypes.length === 0) newTypes = ['blank']; // Always have at least one type
      } else {
        // Add the type if it's not selected
        newTypes = [...currentTypes.filter(t => t !== 'blank'), type]; // Remove blank when adding other types
      }
      
      // Update the cell with multiple types
      handleGridCellChange(editingCell.index, 'types', newTypes);
      
      // Clear content if no text type is selected
      if (!newTypes.includes('text')) {
        handleGridCellChange(editingCell.index, 'content', '');
      }
      
      // Update the editingCell state
      setEditingCell({
        ...editingCell,
        cell: {
          ...editingCell.cell,
          types: newTypes,
          content: !newTypes.includes('text') ? '' : editingCell.cell.content
        }
      });
    }
  }, [editingCell, handleGridCellChange]);
  
  const handleCellContentChange = useCallback((content) => {
    if (editingCell) {
      handleGridCellChange(editingCell.index, 'content', content);
      // Update the editingCell state to reflect the new content
      setEditingCell({
        ...editingCell,
        cell: {
          ...editingCell.cell,
          content: content
        }
      });
    }
  }, [editingCell, handleGridCellChange]);

  const handleCustomModuleSave = useCallback(() => {
    if (customModule.moduleName.trim()) {
      onSave(customModule);
      onClose();
      // Reset custom module
      setCustomModule({
        moduleName: '',
        section: '',
        gridRows: 3,
        gridColumns: 2,
        totalPoints: 5,
        gridCells: []
      });
    }
  }, [customModule, onSave, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="bg-blue-600 text-white p-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            {existingData ? 'Edit Assessment Module' : 'Assessment Modules'}
          </h2>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 p-1 rounded"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('predefined')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'predefined'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Predefined Modules
            </button>
            <button
              onClick={() => setActiveTab('custom')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'custom'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Build Custom Module
            </button>
          </nav>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {activeTab === 'predefined' ? (
            <>
              <div className="mb-4">
                <p className="text-gray-600 text-sm">
                  Select a predefined NREMT assessment module to add to your skill sheet.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {predefinedModules.map((module) => (
                  <PredefinedModuleCard 
                    key={module.id} 
                    module={module} 
                    onSelect={handlePredefinedModuleSelect} 
                  />
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="mb-4">
                <p className="text-gray-600 text-sm">
                  Create your own custom assessment module with specific tasks and criteria.
                </p>
              </div>

              <div className="space-y-4">
                {/* Module Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Module Name
                    </label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., Trauma Assessment"
                      value={customModule.moduleName}
                      onChange={(e) => setCustomModule({ ...customModule, moduleName: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Section
                    </label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., Patient Assessment"
                      value={customModule.section}
                      onChange={(e) => setCustomModule({ ...customModule, section: e.target.value })}
                    />
                  </div>
                </div>

                {/* Grid Configuration */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-blue-900 mb-3">Grid Configuration</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Grid Rows
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                        value={customModule.gridRows}
                        onChange={(e) => setCustomModule({ ...customModule, gridRows: parseInt(e.target.value) || 1 })}
                      />
                      <p className="text-xs text-gray-500 mt-1">Number of rows in the grid</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Grid Columns
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="6"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                        value={customModule.gridColumns}
                        onChange={(e) => setCustomModule({ ...customModule, gridColumns: parseInt(e.target.value) || 1 })}
                      />
                      <p className="text-xs text-gray-500 mt-1">Number of columns in the grid</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Total Points
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="50"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                        value={customModule.totalPoints}
                        onChange={(e) => setCustomModule({ ...customModule, totalPoints: parseInt(e.target.value) || 1 })}
                      />
                      <p className="text-xs text-gray-500 mt-1">Points for entire module</p>
                    </div>
                  </div>
                  
                  {/* Grid Preview */}
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Module Preview (as it will appear in the assessment table)
                    </label>
                    <div className="border border-gray-300 rounded bg-white p-2">
                      {/* Table Header - matching exact main table styling */}
                      <table className="w-full border-collapse border border-gray-400 text-xs">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="border border-gray-400 px-1 py-0 text-left font-semibold w-1/6 align-middle">Section</th>
                            <th className="border border-gray-400 px-1 py-0 text-left font-semibold align-middle" style={{ width: 'calc(100% - 160px)' }}>Task/Criteria</th>
                            <th className="border border-gray-400 px-1 py-0 text-center font-semibold align-middle" style={{ width: '60px' }}>Critical</th>
                            <th className="border border-gray-400 px-1 py-0 text-center font-semibold align-middle" style={{ width: '50px' }}>Points</th>
                            <th className="border border-gray-400 px-1 py-0 text-center font-semibold align-middle" style={{ width: '50px' }}>Score</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            {/* Section Column */}
                            <td className="border border-gray-400 px-1 py-0 align-middle relative h-6">
                              <span className="text-xs">{customModule.section || 'Module Section'}</span>
                            </td>
                            
                            {/* Task/Criteria Column with Grid */}
                            <td className="border border-gray-400 px-1 py-0 align-middle h-6">
                              <div 
                                className="w-full"
                                style={{ 
                                  display: 'grid',
                                  gridTemplateColumns: `repeat(${customModule.gridColumns}, 1fr)`,
                                  gridTemplateRows: `repeat(${customModule.gridRows}, 1fr)`,
                                  minHeight: '60px',
                                  gap: '0'
                                }}
                              >
                                {customModule.gridCells.map((cell, index) => {
                                  const gridRow = Math.floor(index / customModule.gridColumns);
                                  const gridCol = index % customModule.gridColumns;
                                  const isLastRow = gridRow === customModule.gridRows - 1;
                                  const isLastCol = gridCol === customModule.gridColumns - 1;
                                  
                                  return (
                                    <div 
                                      key={index}
                                      className="bg-white text-gray-700 flex items-center justify-center cursor-pointer hover:bg-blue-100 transition-colors"
                                      style={{ 
                                        fontSize: '8px', 
                                        minHeight: '20px',
                                        borderRight: isLastCol ? 'none' : '1px solid #9CA3AF',
                                        borderBottom: isLastRow ? 'none' : '1px solid #9CA3AF',
                                        padding: '0',
                                        margin: '0',
                                        lineHeight: '1.1'
                                      }}
                                      onClick={() => setEditingCell({ index, cell })}
                                    >
                                      {(() => {
                                        const cellTypes = cell.types || [cell.type || 'blank'];
                                        const hasText = cellTypes.includes('text');
                                        const hasCheckbox = cellTypes.includes('checkbox');
                                        
                                        if (hasText && hasCheckbox) {
                                          return (
                                            <div className="flex items-center justify-center px-1 text-xs" style={{ width: '100%' }}>
                                              <span style={{ marginRight: '2px' }}>
                                                {cell.content || ''}
                                              </span>
                                              <span>☐</span>
                                            </div>
                                          );
                                        } else if (hasText) {
                                          return (
                                            <div className="flex items-center justify-center text-xs" style={{ width: '100%' }}>
                                              <span>
                                                {cell.content || ''}
                                              </span>
                                            </div>
                                          );
                                        } else if (hasCheckbox) {
                                          return (
                                            <div className="flex items-center justify-center text-xs" style={{ width: '100%' }}>
                                              <span>☐</span>
                                            </div>
                                          );
                                        } else {
                                          return '';
                                        }
                                      })()}
                                    </div>
                                  );
                                })}
                              </div>
                            </td>
                            
                            {/* Critical Column */}
                            <td className="border border-gray-400 px-1 py-0 text-center align-middle h-6">
                              <div className="w-3 h-3 border border-gray-400 bg-white mx-auto"></div>
                            </td>
                            
                            {/* Points Column */}
                            <td className="border border-gray-400 px-1 py-0 text-center align-middle h-6">
                              <span className="text-xs font-medium">{customModule.totalPoints}</span>
                            </td>
                            
                            {/* Score Column */}
                            <td className="border border-gray-400 px-1 py-0 text-center align-middle h-6">
                              <div className="w-6 h-3 border-b border-gray-400 bg-white mx-auto"></div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="bg-gray-50 p-4 border-t">
          <div className="flex justify-between items-center">
            {activeTab === 'predefined' ? (
              <>
                <div className="text-sm text-gray-600">
                  <span className="font-medium">{predefinedModules.length}</span> predefined modules available
                </div>
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <div className="text-sm text-gray-600">
                  Create custom modules with your specific assessment criteria
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-100 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCustomModuleSave}
                    disabled={!customModule.moduleName.trim()}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                  >
                    Add Module
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Cell Editor Popup */}
      {editingCell && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-60 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl w-80 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Edit Cell {editingCell.index + 1}
              </h3>
              <button
                onClick={() => setEditingCell(null)}
                className="text-gray-400 hover:text-gray-600 p-1 rounded"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cell Types (Select multiple)
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleCellTypeToggle('text')}
                    className={`px-2 py-1 border rounded text-center transition-colors text-xs ${
                      (editingCell.cell.types || [editingCell.cell.type || 'blank']).includes('text')
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {(editingCell.cell.types || [editingCell.cell.type || 'blank']).includes('text') ? '✓ ' : ''}
                    Text
                  </button>
                  <button
                    onClick={() => handleCellTypeToggle('checkbox')}
                    className={`px-2 py-1 border rounded text-center transition-colors text-xs ${
                      (editingCell.cell.types || [editingCell.cell.type || 'blank']).includes('checkbox')
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {(editingCell.cell.types || [editingCell.cell.type || 'blank']).includes('checkbox') ? '✓ ' : ''}
                    Checkbox
                  </button>
                  <button
                    onClick={() => handleCellTypeToggle('blank')}
                    className={`px-2 py-1 border rounded text-center transition-colors text-xs ${
                      (editingCell.cell.types || [editingCell.cell.type || 'blank']).includes('blank')
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {(editingCell.cell.types || [editingCell.cell.type || 'blank']).includes('blank') ? '✓ ' : ''}
                    Blank
                  </button>
                </div>
              </div>

              {(editingCell.cell.types || [editingCell.cell.type || 'blank']).includes('text') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Label Text
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter label text..."
                    value={editingCell.cell.content}
                    onChange={(e) => handleCellContentChange(e.target.value)}
                  />
                </div>
              )}
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setEditingCell(null)}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const PredefinedModuleCard = React.memo(({ module, onSelect }) => {
  return (
    <div
      className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer bg-gray-50"
      onClick={() => onSelect(module)}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-semibold text-gray-900 text-sm mb-1">
            {module.moduleName}
          </h3>
          <p className="text-xs text-blue-600 font-medium">
            Section: {module.section}
          </p>
          <p className="text-xs text-green-600 font-medium mt-1">
            Grid: {module.gridRows}x{module.gridColumns} • {module.totalPoints} points
          </p>
        </div>
        <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs font-medium">
          {module.tasks.length} tasks
        </span>
      </div>

      <div className="space-y-1">
        <p className="text-xs font-medium text-gray-700 mb-2">Tasks include:</p>
        <div className="space-y-1 max-h-32 overflow-y-auto">
          {module.tasks.slice(0, 4).map((task, index) => (
            <div key={index} className="flex items-start gap-2 text-xs text-gray-600">
              <span className="text-blue-500 mt-0.5">•</span>
              <span className={task.critical ? 'font-medium text-red-600' : ''}>
                {task.text}
                {task.critical && <span className="text-red-500 ml-1">(Critical)</span>}
              </span>
            </div>
          ))}
          {module.tasks.length > 4 && (
            <div className="text-xs text-gray-500 italic">
              ...and {module.tasks.length - 4} more tasks
            </div>
          )}
        </div>
      </div>

      <div className="mt-3 pt-3 border-t border-gray-200">
        <div className="flex items-center justify-center text-blue-600 text-sm font-medium">
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Click to add to assessment
        </div>
      </div>
    </div>
  );
});

export default PrintNREMTCustomModuleBuilder;
